import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, ChevronRight, FileText, ShoppingBag } from "lucide-react";

const STATUS_LABEL = {
  submitted: "New",
  accepted_pending_pickup: "Accepted",
  pending_customer_review: "Awaiting Customer",
  customer_accepted: "Customer Re-Accepted",
  completed: "Completed",
};
const STATUS_COLOR = {
  submitted: "var(--ab-warning)",
  accepted_pending_pickup: "var(--ab-primary)",
  pending_customer_review: "var(--ab-accent-red)",
  customer_accepted: "var(--ab-primary)",
  completed: "var(--ab-text-dim)",
};

export default function PurchaseRequestsHub() {
  const nav = useNavigate();
  const [tab, setTab] = useState("open"); // open | closed
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/purchase-requests", { params: { status: tab } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => nav("/pos/dashboard")} className="label-eyebrow mb-6 inline-block" data-testid="pr-hub-back">← Back</button>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-4xl font-black inline-flex items-center gap-3"><ShoppingBag size={26} /> Purchase Requests</h1>
          <div className="text-right">
            <div className="label-eyebrow">{tab === "open" ? "Open Total" : "Closed Total"}</div>
            <div className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }} data-testid="pr-hub-total">${Number(data.total || 0).toFixed(2)}</div>
          </div>
        </div>

        <div className="flex gap-1 mb-6" style={{ borderBottom: "1px solid var(--ab-border)" }}>
          {[
            { k: "open", label: "Open" },
            { k: "closed", label: "Closed" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className="px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
              style={{
                color: tab === t.k ? "var(--ab-primary)" : "var(--ab-text-dim)",
                borderBottom: tab === t.k ? "2px solid var(--ab-primary)" : "2px solid transparent",
                marginBottom: "-1px",
              }}
              data-testid={`pr-hub-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "closed" && (
          <div className="text-xs mb-3" style={{ color: "var(--ab-text-muted)" }}>
            Combined view: {data.purchase_requests || 0} completed reservation(s) + {data.pos_sales || 0} POS sale(s). Refunds are listed but excluded from the total.
          </div>
        )}

        {loading ? <Loader2 className="animate-spin" /> : (
          <div className="surface divide-y" style={{ borderColor: "var(--ab-border)" }}>
            {data.rows.length === 0 && <div className="p-6 text-sm" style={{ color: "var(--ab-text-muted)" }} data-testid="pr-hub-empty">Nothing here yet.</div>}
            {data.rows.map((r) => {
              const isPR = tab === "open" || r.kind === "purchase_request";
              const isClosedSale = tab === "closed" && r.kind === "pos_sale";
              const status = r.status || "submitted";
              const url = isClosedSale ? `/pos/transactions/${r.id}` : `/pos/purchase-requests/${r.id}`;
              return (
                <Link key={r.id} to={url} className="flex items-center px-5 py-4 hover:bg-[var(--ab-surface-2)] transition-colors" data-testid={`pr-hub-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{r.customer_name || "Walk-In"}</span>
                      <span className="font-mono text-[10px] px-1.5 py-0.5" style={{ background: "var(--ab-surface-2)", color: "var(--ab-text-dim)" }}>
                        {r.ref || r.id?.slice(0, 8)}
                      </span>
                      {isClosedSale && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5" style={{ background: "rgba(245,158,11,0.15)", color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }}>POS</span>
                      )}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--ab-text-dim)" }}>
                      {tab === "open"
                        ? (r.items?.length || 0) + " item(s) · " + new Date(r.created_at).toLocaleString()
                        : (r.items_count || 0) + " item(s) · " + new Date(r.closed_at || Date.now()).toLocaleString()}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold mr-4" style={{ color: STATUS_COLOR[status] || "var(--ab-text-dim)" }}>
                    {STATUS_LABEL[status] || status}
                  </span>
                  <div className="font-mono font-bold mr-3" style={{ color: "var(--ab-primary)" }}>${Number(r.total || 0).toFixed(2)}</div>
                  <ChevronRight size={16} style={{ color: "var(--ab-text-dim)" }} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
